import { useState } from "react";
import { Link } from "react-router-dom";
import PublicCartSidebar from "../components/PublicCartSidebar";
import PublicFooter from "../components/PublicFooter";
import PublicNavbar from "../components/PublicNavbar";
import {
  CartItem,
  isMissingDeliveryAddress,
  PublicDeliveryInfo,
} from "../types/store";
import {
  buildServiceWhatsAppMessage,
  openWhatsApp,
  WHATSAPP_DIAGNOSTICO_EXPRESS,
  WHATSAPP_SALUD_EN_CASA,
} from "../utils/whatsapp";

const SALUD_CASA_OPTIONS = [
  {
    id: "medicamentos",
    title: "Servicio de administracion de medicamentos",
    subtitle: "(endovenoso, subcutaneo, intramuscular)",
    price: 7,
  },
  {
    id: "sueros",
    title: "Administracion de sueros",
    subtitle: null,
    price: 70,
  },
] as const;

type SaludCasaOptionId = (typeof SALUD_CASA_OPTIONS)[number]["id"];

const SERVICES = [
  {
    key: "salud-en-casa",
    name: "Salud en casa",
    description: "Atencion medica y cuidados desde tu hogar.",
    icon: "/icons/salud-casa.png",
    to: null,
    opensModal: true,
  },
  {
    key: "diagnostico-express",
    name: "Diagnostico Express",
    description: "Resultados rapidos y seguimiento de salud.",
    icon: "/icons/diagnostico.png",
    to: null,
    opensModal: false,
  },
  {
    key: "farmacia",
    name: "Farmacia",
    description: "Compra medicamentos y productos de cuidado personal.",
    icon: "/icons/farmacia.png",
    to: "/productos",
    opensModal: false,
  },
] as const;

function ServiceCardContent({
  service,
}: {
  service: (typeof SERVICES)[number];
}) {
  return (
    <>
      <div className="public-service-icon">
        <img src={service.icon} alt={service.name} />
      </div>
      <p className="public-service-name">{service.name}</p>
      <span className="public-service-description">{service.description}</span>
    </>
  );
}

type PublicHomePageProps = {
  userName: string;
  deliveryInfo: PublicDeliveryInfo;
  cartItems: CartItem[];
  cartUnits: number;
  isCartOpen: boolean;
  onOpenCart: () => void;
  onCloseCart: () => void;
  onRemoveCartItem: (itemId: string) => void;
  onDeliveryChange: (delivery: PublicDeliveryInfo) => void;
  onLogout: () => void;
  onCheckout: () => void;
};

export default function PublicHomePage({
  userName,
  deliveryInfo,
  cartItems,
  cartUnits,
  isCartOpen,
  onOpenCart,
  onCloseCart,
  onRemoveCartItem,
  onDeliveryChange,
  onLogout,
  onCheckout,
}: PublicHomePageProps) {
  const [isSaludCasaModalOpen, setIsSaludCasaModalOpen] = useState(false);
  const [selectedSaludCasaOption, setSelectedSaludCasaOption] =
    useState<SaludCasaOptionId | null>(null);

  const openSaludCasaModal = () => {
    setSelectedSaludCasaOption(null);
    setIsSaludCasaModalOpen(true);
  };

  const closeSaludCasaModal = () => {
    setIsSaludCasaModalOpen(false);
    setSelectedSaludCasaOption(null);
  };

  const handleSaludCasaContinue = () => {
    if (!selectedSaludCasaOption) {
      return;
    }

    const selectedOption = SALUD_CASA_OPTIONS.find(
      (option) => option.id === selectedSaludCasaOption,
    );
    if (!selectedOption) {
      return;
    }

    const serviceDetail = selectedOption.subtitle
      ? `${selectedOption.title} ${selectedOption.subtitle}`
      : selectedOption.title;

    openWhatsApp(
      WHATSAPP_SALUD_EN_CASA,
      buildServiceWhatsAppMessage({
        userName,
        deliveryInfo,
        serviceName: "Salud en casa",
        serviceDetail,
      }),
    );
    closeSaludCasaModal();
  };

  const handleDiagnosticoExpressClick = () => {
    openWhatsApp(
      WHATSAPP_DIAGNOSTICO_EXPRESS,
      buildServiceWhatsAppMessage({
        userName,
        deliveryInfo,
        serviceName: "Diagnostico Express",
      }),
    );
  };

  return (
    <main className="public-page-shell">
      <PublicNavbar
        deliveryInfo={deliveryInfo}
        cartUnits={cartUnits}
        onOpenCart={onOpenCart}
        onDeliveryChange={onDeliveryChange}
        requireAddress={isMissingDeliveryAddress(deliveryInfo.address)}
      />

      <section className="card content-card public-home-card">
        <div className="public-greeting-block">
          <h2>Hola {userName}</h2>
          <p className="subtitle">Que servicio necesitas hoy?</p>
        </div>

        <div className="public-services-grid">
          {SERVICES.map((service) =>
            service.to ? (
              <Link
                key={service.key}
                to={service.to}
                className="public-service-card is-active"
                aria-label={service.name}
              >
                <ServiceCardContent service={service} />
              </Link>
            ) : service.opensModal ? (
              <button
                key={service.key}
                type="button"
                className="public-service-card is-active"
                aria-label={service.name}
                onClick={openSaludCasaModal}
              >
                <ServiceCardContent service={service} />
              </button>
            ) : (
              <button
                key={service.key}
                type="button"
                className="public-service-card is-active"
                aria-label={service.name}
                onClick={handleDiagnosticoExpressClick}
              >
                <ServiceCardContent service={service} />
              </button>
            ),
          )}
        </div>
      </section>

      {isSaludCasaModalOpen ? (
        <div
          className="modal-overlay"
          onClick={closeSaludCasaModal}
          role="presentation"
        >
          <div
            className="modal-content modal-content-sm diagnostico-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="salud-casa-modal-title"
          >
            <div className="modal-header">
              <h3 id="salud-casa-modal-title">Salud en casa</h3>
              <button
                type="button"
                className="modal-close"
                onClick={closeSaludCasaModal}
              >
                x
              </button>
            </div>

            <p className="subtitle diagnostico-modal-subtitle">
              Elige el servicio que necesitas:
            </p>

            <div
              className="diagnostico-options"
              role="radiogroup"
              aria-label="Servicios de salud en casa"
            >
              {SALUD_CASA_OPTIONS.map((option) => {
                const isSelected = selectedSaludCasaOption === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    className={`diagnostico-option-card ${isSelected ? "is-selected" : ""}`}
                    onClick={() => setSelectedSaludCasaOption(option.id)}
                  >
                    <span
                      className="diagnostico-option-radio"
                      aria-hidden="true"
                    />
                    <div className="diagnostico-option-content">
                      <p className="diagnostico-option-title">{option.title}</p>
                      {option.subtitle ? (
                        <p className="diagnostico-option-subtitle">
                          {option.subtitle}
                        </p>
                      ) : null}
                      <p className="diagnostico-option-price">
                        S/. {option.price.toFixed(0)} <span>+ Movilidad</span>
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="diagnostico-continue-btn"
              disabled={!selectedSaludCasaOption}
              onClick={handleSaludCasaContinue}
            >
              Continuar
            </button>
          </div>
        </div>
      ) : null}

      <PublicFooter onLogout={onLogout} />

      <PublicCartSidebar
        isOpen={isCartOpen}
        cartItems={cartItems}
        onClose={onCloseCart}
        onRemoveItem={onRemoveCartItem}
        onCheckout={onCheckout}
      />
    </main>
  );
}
